import { Search } from 'lucide-react'
import { useSearchParams } from 'react-router'

import { StatusPill } from '@/components/StatusPill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { useDebouncedInput } from '@/hooks/useDebouncedInput'
import { cn } from '@/lib/utils'
import {
  BOOKKEEPING_STATUS_LABELS,
  BookkeepingStatus,
  useBookkeeperCustomers,
} from '@/models/bookkeeperCustomer'
import { CustomerListCard, NAME_COLUMN_CLASS, STATUS_COLUMN_CLASS } from './CustomerListCard'

export function BookkeeperCustomers() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(searchParams.get('page') || '1')
  const search = searchParams.get('search') || ''
  const status = searchParams.get('status') || ''
  const { customers, count, hasNextPage, hasPreviousPage, isLoading, isFetching, isError, refetch } =
    useBookkeeperCustomers(page, search, status)

  function updateParams(overrides: { search?: string; status?: string; page?: number }) {
    const next = { search, status, page: 1, ...overrides }
    const params: Record<string, string> = { product: 'bookkeeper' }
    if (next.search) params.search = next.search
    if (next.status) params.status = next.status
    if (next.page > 1) params.page = String(next.page)
    setSearchParams(params)
  }

  const { inputValue, setInputValue } = useDebouncedInput(search, (value) => updateParams({ search: value }))
  const staleRows = isFetching && !isLoading

  return (
    <CustomerListCard
      filters={
        <>
          <div className="relative w-full sm:max-w-sm sm:flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              aria-label="Search Bookkeeper customers"
              placeholder="Search by business, name, email, phone, or DOT..."
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              className="h-11 border-neutral-300 pl-9 text-sm"
            />
          </div>
          <Select
            value={status || 'all'}
            onValueChange={(value) => updateParams({ status: value === 'all' ? '' : value })}
          >
            <SelectTrigger
              aria-label="Bookkeeping status"
              className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[180px]"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.values(BookkeepingStatus).map((value) => (
                <SelectItem key={value} value={value}>
                  {BOOKKEEPING_STATUS_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      }
    >
      {isError ? (
        <div role="alert" className="flex flex-col items-center gap-3 py-6">
          <p>Could not load customers</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={cn('font-semibold', NAME_COLUMN_CLASS)}>Name</TableHead>
                <TableHead className={cn('font-semibold', STATUS_COLUMN_CLASS)}>Status</TableHead>
                <TableHead className="font-semibold">Users</TableHead>
                <TableHead className="font-semibold">Phone</TableHead>
                <TableHead className="font-semibold">DOT</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody inert={staleRows} className={cn('transition-opacity', staleRows && 'opacity-60')}>
              {isLoading && <TableSkeletonRows columns={5} />}
              {!isLoading && customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="pt-5 text-center">
                    No customers found
                  </TableCell>
                </TableRow>
              )}
              {customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell className="font-medium">{customer.legalName}</TableCell>
                  <TableCell>
                    <StatusPill status={customer.bookkeepingStatus || BookkeepingStatus.UNKNOWN} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-2">
                      {customer.users.length === 0 && (
                        <span className="text-muted-foreground">No linked users</span>
                      )}
                      {customer.users.map((user) => (
                        <div key={user.email}>
                          {user.name && <div>{user.name}</div>}
                          <div className="text-muted-foreground">{user.email}</div>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{customer.phoneNumber || '—'}</TableCell>
                  <TableCell>{customer.dotNumber || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(hasPreviousPage || hasNextPage) && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-muted-foreground text-sm">
                Page {page} ({count} total)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPreviousPage || isFetching}
                  onClick={() => updateParams({ page: page - 1 })}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNextPage || isFetching}
                  onClick={() => updateParams({ page: page + 1 })}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </CustomerListCard>
  )
}
